import { Cancel as CancelIcon, Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material'
import { IconButton, Tooltip } from '@mui/material'
import { GridRowModes, type GridRowId, type GridRowModesModel } from '@mui/x-data-grid-pro'
import React from 'react'

import type { GridAction, GridRow } from './types'

// Replaces `{column}` tokens with that row's value. `{id}` is special-cased to
// the configured primary column (rowIdField) so it stays correct even when the
// row id lives under a non-`id` key; any other `{key}` maps to row[key]. Used
// for both link templates and confirm messages.
const interpolate = (template: string, row: GridRow, rowIdField: string): string =>
	template.replace(/\{(\w+)\}/g, (_match, key: string) => {
		const value = key === 'id' ? row[rowIdField] : row[key]
		return value === null || value === undefined ? '' : String(value)
	})

const resolveUrl = (action: GridAction, row: GridRow, rowIdField: string): string | null => {
	const links = row.__actionLinks
	if (links !== null && links !== undefined && typeof links === 'object' && action.key in links) {
		const url = (links as Record<string, string>)[action.key]
		return typeof url === 'string' ? url : null
	}
	if (action.linkTemplate !== null) {
		return interpolate(action.linkTemplate, row, rowIdField)
	}
	return null
}

// Project color variables exposed in `assets/css/base_legacy.less` (single
// source of truth lives in LESS). Falls through to MUI's palette if the
// var isn't set so the grid still renders something usable.
const COLOR_EDIT = 'var(--c-color-blue, #609EEE)'
const COLOR_SAVE = 'var(--c-color-green, #35C02D)'
const COLOR_CANCEL = 'var(--c-color-orange, #F79527)'
const COLOR_DELETE = 'var(--c-color-red, #EE6060)'
const COLOR_DEFAULT = 'inherit'

const colorForAction = (action: GridAction): string => {
	const icon = action.icon ?? ''
	if (icon.includes('trash') || icon.includes('times-circle') || action.key.includes('delete')) {
		return COLOR_DELETE
	}
	if (icon.includes('edit') || icon.includes('pencil') || action.key === 'edit') {
		return COLOR_EDIT
	}
	if (icon.includes('save') || icon.includes('check')) {
		return COLOR_SAVE
	}
	return COLOR_DEFAULT
}

const NON_FOCUS_SX = {
	'&:focus, &:focus-visible, &.Mui-focusVisible': { outline: 'none', boxShadow: 'none' },
	'& > i': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '1em',
		height: '1em',
		fontSize: '1rem',
		lineHeight: 1,
	},
} as const

const iconSx = (color: string) => ({ color, ...NON_FOCUS_SX })

type Props = {
	rowId: GridRowId
	row: GridRow
	rowIdField: string
	rowModesModel: GridRowModesModel
	inlineEditEnabled: boolean
	columnActions: GridAction[]
	onEdit: (id: GridRowId) => void
	onSave: (id: GridRowId) => void
	onCancel: (id: GridRowId) => void
	onSignal: (actionKey: string, rowId: string | number) => void
}

export const RowActionsCell: React.FC<Props> = ({
	rowId,
	row,
	rowIdField,
	rowModesModel,
	inlineEditEnabled,
	columnActions,
	onEdit,
	onSave,
	onCancel,
	onSignal,
}) => {
	const mode = rowModesModel[rowId]?.mode ?? GridRowModes.View
	const isEditing = mode === GridRowModes.Edit

	const stop = (event: React.MouseEvent<HTMLButtonElement>): void => {
		event.stopPropagation()
	}

	const triggerAction =
		(action: GridAction) =>
		(event: React.MouseEvent<HTMLButtonElement>): void => {
			event.stopPropagation()
			if (action.confirmMessage !== null) {
				const message = interpolate(action.confirmMessage, row, rowIdField)
				if (!window.confirm(message)) {
					return
				}
			}
			if (action.type === 'link') {
				const url = resolveUrl(action, row, rowIdField)
				if (url !== null) {
					window.location.href = url
				}
				return
			}
			if (action.type === 'ajax') {
				const url = resolveUrl(action, row, rowIdField)
				if (url !== null) {
					// @ts-ignore — jQuery nette ajax is globally available via webpack ProvidePlugin
					$.nette.ajax({ url, method: 'GET' })
				}
				return
			}
			if (action.type === 'signal' && (typeof rowId === 'string' || typeof rowId === 'number')) {
				onSignal(action.key, rowId)
			}
		}

	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'flex-start',
				gap: 4,
				height: '100%',
			}}
		>
			{isEditing && (
				<>
					<Tooltip title="Save">
						<IconButton
							size="small"
							tabIndex={-1}
							disableFocusRipple
							onMouseDown={stop}
							onClick={(e) => {
								e.stopPropagation()
								onSave(rowId)
							}}
							sx={iconSx(COLOR_SAVE)}
						>
							<SaveIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Cancel">
						<IconButton
							size="small"
							tabIndex={-1}
							disableFocusRipple
							onMouseDown={stop}
							onClick={(e) => {
								e.stopPropagation()
								onCancel(rowId)
							}}
							sx={iconSx(COLOR_CANCEL)}
						>
							<CancelIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</>
			)}
			{!isEditing && inlineEditEnabled && (
				<Tooltip title="Edit">
					<IconButton
						size="small"
						tabIndex={-1}
						disableFocusRipple
						onMouseDown={stop}
						onClick={(e) => {
							e.stopPropagation()
							onEdit(rowId)
						}}
						sx={iconSx(COLOR_EDIT)}
					>
						<EditIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			)}
			{!isEditing &&
				columnActions
					.filter((action) => !(row.__hiddenActions ?? []).includes(action.key))
					.map((action) => (
						<Tooltip key={action.key} title={action.label}>
							<IconButton
								size="small"
								tabIndex={-1}
								disableFocusRipple
								onMouseDown={stop}
								onClick={triggerAction(action)}
								sx={iconSx(colorForAction(action))}
							>
								{action.icon !== null ? <i className={action.icon} /> : action.label.charAt(0)}
							</IconButton>
						</Tooltip>
					))}
		</div>
	)
}
